/**
 * Per-field PII mask formatting.
 *
 * Each helper returns the *display* string the SPA paints when a field is
 * masked. The unmasked value never travels through this module - reveal is
 * a UI state change, not a transform.
 *
 * Formats per ADR-013 PII Masking and Audit-on-Reveal
 * (https://suitsme.atlassian.net/wiki/spaces/MUR/pages/2497871914) (PII masking interaction
 * spec) and the PR-E2 firm inputs:
 *
 *   legacy_username      -> full mask `(hidden)` (short field, no partial reveal)
 *   bank_account_number  -> last 4 visible: `**** **** 1234` (fall back to
 *                           full mask if shorter than 4 chars)
 *   sort_code            -> last 4 visible (same fallback)
 *   tribe_epm_id         -> last 4 visible (same fallback)
 *   iban                 -> first 4 + last 4 visible: `GB29 **** **** **** **34`
 *                           (full mask if shorter than 8 chars; whitespace in the
 *                           source value is stripped before grouping so the input
 *                           shape does not leak)
 *
 * Holder PII shapes (SM2-7) per `01-data-design.md`:
 *
 *   first_name / last_name / date_of_birth / nationality
 *                        -> full mask `(hidden)` (high-sensitivity identity)
 *   email_address        -> first char + masked local + domain TLD only:
 *                           `j****@****.uk` (full mask if not email-shaped)
 *   mobile_no            -> last 3 visible: `*** *** 789` (full mask if < 3)
 *   full_phone_number    -> last 3 visible (same)
 *   building_name / building_no / street
 *                        -> full mask (address lines are high-sensitivity)
 *   postcode             -> outward code hidden, last 2 visible: `**** 2X`
 *   vulnerable           -> ALWAYS full mask (Art. 9; never partial-reveal)
 *   vulnerability_rating -> ALWAYS full mask (Art. 9; never partial-reveal)
 *
 * All formatters return null when the upstream value is null - the SPA
 * renders an `n/a` placeholder rather than a mask in that case.
 */
import type { PiiField } from './bff-client'

/** Mask format identifier (used by tests and the MaskedField component). */
export type MaskKind =
  | 'full'
  | 'last4'
  | 'first4-last4'
  | 'partial-email'
  | 'partial-phone'
  | 'partial-postcode'

const FULL_MASK = '(hidden)'

export function maskKindFor(field: PiiField): MaskKind {
  switch (field) {
    // Account fields (PR-E2).
    case 'legacy_username':
      return 'full'
    case 'bank_account_number':
    case 'sort_code':
    case 'tribe_epm_id':
      return 'last4'
    case 'iban':
      return 'first4-last4'
    // Holder fields (SM2-7).
    case 'first_name':
    case 'last_name':
    case 'date_of_birth':
    case 'nationality':
    case 'building_name':
    case 'building_no':
    case 'street':
    // Art. 9 special-category - never partially revealed.
    case 'vulnerable':
    case 'vulnerability_rating':
      return 'full'
    case 'email_address':
      return 'partial-email'
    case 'mobile_no':
    case 'full_phone_number':
      return 'partial-phone'
    case 'postcode':
      return 'partial-postcode'
  }
}

/**
 * Format the masked display string for a field. The value is read but its
 * *content* is never echoed - only a small, low-entropy tail (or the
 * email TLD) is surfaced per ADR-013 and `01-data-design.md`.
 */
export function formatMasked(field: PiiField, value: string | null): string | null {
  if (value === null) return null
  const kind = maskKindFor(field)
  switch (kind) {
    case 'full':
      return FULL_MASK
    case 'last4':
      return formatLast4(value)
    case 'first4-last4':
      return formatFirst4Last4(value)
    case 'partial-email':
      return formatEmail(value)
    case 'partial-phone':
      return formatPhone(value)
    case 'partial-postcode':
      return formatPostcode(value)
  }
}

/**
 * Email: surface the first local char and the top-level domain only.
 * `jane.doe@example.co.uk` -> `j****@****.uk`. Anything that is not
 * email-shaped (no single `@`, no dotted domain) falls back to a full
 * mask rather than echoing the raw value.
 */
function formatEmail(value: string): string {
  const at = value.indexOf('@')
  if (at <= 0 || value.indexOf('@', at + 1) !== -1) return FULL_MASK
  const local = value.slice(0, at)
  const domain = value.slice(at + 1)
  const lastDot = domain.lastIndexOf('.')
  if (lastDot <= 0 || lastDot === domain.length - 1) return FULL_MASK
  const tld = domain.slice(lastDot + 1)
  const firstLocal = local[0] ?? ''
  return `${firstLocal}****@****.${tld}`
}

/**
 * Phone: surface the last 3 digits only. `+44 7700 900123` -> `*** *** 123`.
 * Falls back to a full mask when fewer than 3 chars are present.
 */
function formatPhone(value: string): string {
  const cleaned = value.replace(/\s+/g, '')
  if (cleaned.length < 3) return FULL_MASK
  return `*** *** ${cleaned.slice(-3)}`
}

/**
 * Postcode: outward code fully hidden, last 2 chars of the inward code
 * surfaced. `SW1A 1AA` -> `**** AA`. Falls back to a full mask when
 * fewer than 2 chars are present.
 */
function formatPostcode(value: string): string {
  const trimmed = value.trim()
  if (trimmed.length < 2) return FULL_MASK
  return `**** ${trimmed.slice(-2)}`
}

function formatLast4(value: string): string {
  // For short fields fall back to a full mask rather than echoing the
  // whole thing - the spec is "all but last 4 chars where present".
  const cleaned = value.replace(/\s+/g, '')
  if (cleaned.length < 4) return FULL_MASK
  const head = '*'.repeat(Math.max(cleaned.length - 4, 4))
  // Group head into spaced quartets for legibility while still being
  // visibly a mask (e.g. `**** **** 1234`). Keep the last 4 verbatim.
  const grouped = chunk(head, 4).join(' ')
  return `${grouped} ${cleaned.slice(-4)}`
}

function formatFirst4Last4(value: string): string {
  // IBAN may contain spaces in source form (`GB29 NWBK ...`). Strip
  // whitespace so the grouping does not leak the input shape.
  const cleaned = value.replace(/\s+/g, '')
  if (cleaned.length < 8) return FULL_MASK
  const head = cleaned.slice(0, 4)
  const tail = cleaned.slice(-4)
  // Middle is everything between head and the last 2 visible chars
  // (we surface only the final two pair-aligned chars per the spec
  // `GB29 **** **** **** **34`).
  const middleLen = cleaned.length - head.length - 2
  const middle = '*'.repeat(Math.max(middleLen, 4))
  const middleGrouped = chunk(middle, 4).join(' ')
  return `${head} ${middleGrouped} ${'**'}${tail.slice(-2)}`
}

function chunk(input: string, size: number): string[] {
  const out: string[] = []
  for (let i = 0; i < input.length; i += size) {
    out.push(input.slice(i, i + size))
  }
  return out
}
